import {
  BLACK,
  type Color,
  FLAG_ACTION,
  FLAG_CAPTURE,
  PAWN,
  WHITE,
  colorBit,
  encodeMove,
  moveFrom,
  moveTo,
  other,
  parseSquare,
  squareName,
} from '../board.ts';
import { FenError } from '../notation.ts';
import { type MoveContext, draw } from '../rules.ts';
import type { BotContext, Position, Variant } from '../types.ts';
import { type SideMove, cellName, formatCells, parseCells, sideMovesFrom } from './sideboard.ts';

// Day 15 (draft). Every move on the big board has to be won first, in a lightning duel on a
// 3×3 board with three pawns each (Hexapawn). Duel pawns step forward and capture diagonally
// forward; you win a duel by reaching the far row, taking every enemy pawn, or leaving your
// opponent without a move. The winner makes the next move on the big board, so winning
// duels in a row means moving in a row. The player who just moved starts the next duel (the
// second player wins a perfectly played duel, so perfect duellists simply alternate). Since
// a player can get several moves in a row there's no check: capture the king to win.
//
// The quick version: duels on 3×3 last six moves at most; 4×4 duels would be the long one.
//
// Duel moves are actions whose from/to name the cell as a square of the corner of a chessboard
// (a1–c3), so they read naturally in UCI ("!b1b2").
//
// ext: [phase (0 duel, 1 big board), en passant square saved during the duel, 9 duel cells]

export const DUEL_SIZE = 3;
const PHASE = 0;
const EP = 1;
const CELLS = 2;

const START_CELLS = 'ppp/3/PPP';
export const DUEL_FEN = `rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR b KQkq - 0 1 d ${START_CELLS} -`;

export function duelCells(pos: { ext: Int32Array | null }): Int32Array {
  return pos.ext!.subarray(CELLS, CELLS + DUEL_SIZE * DUEL_SIZE);
}

export function duelPhase(pos: { ext: Int32Array | null }): boolean {
  return pos.ext?.[PHASE] === 0;
}

/** Duel cell index ⇄ the square naming it in moves. */
export const cellSquare = (i: number) => Math.floor(i / DUEL_SIZE) * 8 + (i % DUEL_SIZE);
export const squareCell = (sq: number) => (sq >> 3) * DUEL_SIZE + (sq & 7);

function resetDuel(ext: Int32Array) {
  for (let f = 0; f < DUEL_SIZE; f++) {
    ext[CELLS + f] = PAWN | colorBit(WHITE);
    ext[CELLS + DUEL_SIZE + f] = 0;
    ext[CELLS + 2 * DUEL_SIZE + f] = PAWN | colorBit(BLACK);
  }
}

function duelMoves(cells: ArrayLike<number>, c: Color): SideMove[] {
  const out: SideMove[] = [];
  const bit = colorBit(c);
  for (let i = 0; i < DUEL_SIZE * DUEL_SIZE; i++) if (cells[i] & bit) sideMovesFrom(cells, DUEL_SIZE, DUEL_SIZE, i, c, out);
  return out;
}

/** Whether the en passant square `ep` can be used by `c` (only against the other side's pawn). */
function epFor(ep: number, c: Color): number {
  if (ep < 0) return -1;
  return (ep >> 3 === 2 && c === BLACK) || (ep >> 3 === 5 && c === WHITE) ? ep : -1;
}

function makeDuel(bigBoardOnly: boolean): Variant {
  return {
    id: bigBoardOnly ? 'duel-board' : 'duel',
    info: {
      name: 'Duel Chess',
      but: 'you duel for every move',
      tagline: 'Before every move there’s a lightning duel on a tiny board. Win it and the move is yours.',
      summary: 'Before each move you fight a **lightning duel** on a 3×3 board. The winner moves on the big board. Keep winning, keep moving.',
      rules: [
        'Before each move on the big board you play a duel on a 3×3 board, three pawns each.',
        'Duel pawns step forward and capture diagonally forward. Win by reaching the far row, taking every enemy pawn, or leaving your opponent with no move.',
        'The winner makes the next move on the big board. Win several duels in a row and you move several times in a row.',
        'The player who just moved starts the next duel. The very first duel is started by Black.',
        'There’s no check: capture the king to win. Stalemate, repetition and the 50-move rule draw.',
      ],
      accent: '#4cc9f0',
      emoji: '⚔️',
      bots: ['Squire', 'Fencer', 'Gladiator', 'Duelist', 'Musketeer'],
    },
    startFen: DUEL_FEN,
    checks: false,
    extSize: CELLS + DUEL_SIZE * DUEL_SIZE,

    parseExtra(fields, pos) {
      const [phase = 'd', cells = START_CELLS, ep = '-'] = fields;
      if (phase !== 'd' && phase !== 'm') throw new FenError('Bad duel phase');
      const ext = pos.ext!;
      ext[PHASE] = phase === 'm' ? 1 : 0;
      parseCells(cells, DUEL_SIZE, DUEL_SIZE, ext, CELLS, [PAWN]);
      ext[EP] = ep === '-' ? -1 : parseSquare(ep);
    },

    formatExtra(pos) {
      const ext = pos.ext!;
      return [ext[PHASE] ? 'm' : 'd', formatCells(duelCells(pos), DUEL_SIZE, DUEL_SIZE), ext[EP] >= 0 ? squareName(ext[EP]) : '-'];
    },

    replaceMoves(pos, out, capturesOnly) {
      if (pos.ext![PHASE] !== 0) return false;
      for (const m of duelMoves(duelCells(pos), pos.turn)) {
        if (capturesOnly && !m.capture && !m.promotes) continue;
        out.push(encodeMove(cellSquare(m.from), cellSquare(m.to), 0, FLAG_ACTION | (m.capture ? FLAG_CAPTURE : 0)));
      }
      return true;
    },

    afterMove(ctx: MoveContext) {
      const { next, prev, us, them, move } = ctx;
      const ext = next.ext!;
      if (ext[PHASE] === 1) {
        if (bigBoardOnly) return;
        // A move on the big board: the mover starts the next duel.
        ext[PHASE] = 0;
        ext[EP] = next.ep;
        resetDuel(ext);
        next.turn = us;
        return;
      }
      next.halfmove = prev.halfmove;
      const cells = duelCells(next);
      const from = squareCell(moveFrom(move));
      const to = squareCell(moveTo(move));
      cells[to] = cells[from];
      cells[from] = 0;
      const farRow = Math.floor(to / DUEL_SIZE) === (us === WHITE ? DUEL_SIZE - 1 : 0);
      let theirs = 0;
      for (const p of cells) if (p & colorBit(them)) theirs++;
      if (farRow || theirs === 0 || duelMoves(cells, them).length === 0) {
        ctx.fx('duel-won', 0);
        resetDuel(ext);
        ext[PHASE] = 1;
        next.turn = us;
        next.ep = epFor(ext[EP], us);
        ext[EP] = -1;
        return;
      }
      next.turn = them;
    },

    nextTurn(ctx) {
      return ctx.next.turn;
    },

    noMoves() {
      return draw('stalemate');
    },

    actionSan(pos, move) {
      const cells = duelCells(pos);
      const from = squareCell(moveFrom(move));
      const to = squareCell(moveTo(move));
      return `⚔ ${cellName(from, DUEL_SIZE)}${cells[to] ? 'x' : '–'}${cellName(to, DUEL_SIZE)}`;
    },

    botMove: bigBoardOnly ? undefined : (pos, bot) => duelBot(pos, bot),
  };
}

export const duel: Variant = makeDuel(false);
/** The big board alone, moves alternating, for the computer's search. */
export const duelBoard: Variant = makeDuel(true);

/** Whether the side to move wins this duel with best play. */
const memo = new Map<string, boolean>();
function duelWins(cells: number[], c: Color): boolean {
  const key = cells.join(',') + c;
  const known = memo.get(key);
  if (known !== undefined) return known;
  let result = false;
  for (const m of duelMoves(cells, c)) {
    const next = cells.slice();
    next[m.to] = next[m.from];
    next[m.from] = 0;
    const theirs = next.filter((p) => p & colorBit(other(c))).length;
    if (m.promotes || theirs === 0 || duelMoves(next, other(c)).length === 0 || !duelWins(next, other(c))) {
      result = true;
      break;
    }
  }
  memo.set(key, result);
  return result;
}

/** How often each level plays a random duel move instead of the best one. */
const DUEL_SLIPS = [0.5, 0.3, 0.15, 0.05, 0];

function duelBot(pos: Position, bot: BotContext): number | null {
  if (pos.ext![PHASE] === 1) return bot.search(duelBoard, pos);
  const cells = Array.from(duelCells(pos));
  const us = pos.turn;
  const moves = duelMoves(cells, us);
  if (!moves.length) return null;
  const encode = (m: SideMove) => encodeMove(cellSquare(m.from), cellSquare(m.to), 0, FLAG_ACTION | (m.capture ? FLAG_CAPTURE : 0));
  const slip = DUEL_SLIPS[Math.max(0, Math.min(4, bot.level - 1))];
  if (bot.random() < slip) return encode(moves[Math.floor(bot.random() * moves.length)]);
  for (const m of moves) {
    const next = cells.slice();
    next[m.to] = next[m.from];
    next[m.from] = 0;
    if (m.promotes || !next.some((p) => p & colorBit(other(us))) || duelMoves(next, other(us)).length === 0 || !duelWins(next, other(us))) {
      return encode(m);
    }
  }
  // Lost with best play: make it last.
  return encode(moves[Math.floor(bot.random() * moves.length)]);
}
